import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  User,
  CreditCard,
  FileText,
  Settings,
  LogOut,
  Trash2,
  Crown,
  BarChart3,
  Calendar,
  Zap,
  Shield,
  AlertTriangle,
  X,
  Check,
  ChevronRight,
  Download,
  ExternalLink,
  History,
  ChevronDown,
  Star,
  MessageCircle,
  FlaskConical
} from 'lucide-react';
import { showToast } from './Toast';
import ModelLogo from './ModelLogo';
import {
  trackEvent,
  trackPageView,
  trackExport,
  trackLogout
} from '../utils/GoogleAnalytics';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

/**
 * UserProfile - Complete & Responsive
 * ✅ UPDATED: GA events + Accessibility improvements
 * ✅ ADDED: History tab to match dropdown menu
 * ✅ FIXED: Mobile dropdown with "Tap to switch" style
 * ✅ FIXED: Dynamic credit packs from DB
 * ✅ FIXED: Removed payment section & simplified history
 */

// ==========================================
// CONFIRM MODAL
// ==========================================
function ConfirmModal({ isOpen, title, message, onConfirm, onCancel, type = 'danger' }) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex items-start space-x-4">
          <div className={`p-3 rounded-full flex-shrink-0 ${
            type === 'danger' ? 'bg-red-100' : 'bg-yellow-100'
          }`}>
            <AlertTriangle
              className={type === 'danger' ? 'text-red-600' : 'text-yellow-600'}
              size={24}
              aria-hidden="true"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="modal-title" className="text-lg font-bold text-gray-900">{title}</h3>
            <p className="text-gray-600 mt-1 text-sm whitespace-pre-line">{message}</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-4 py-2 rounded-lg font-medium text-white transition-colors ${
              type === 'danger'
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// MAIN COMPONENT
// ==========================================
function UserProfile({ user, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Tab configuration - ✅ ADDED History tab
  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'history', label: 'Usage History', icon: History },
    { id: 'billing', label: 'Credits & Plans', icon: Crown },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  // Get active tab from URL or default to 'overview'
  const validTabs = tabs.map(t => t.id);
  const tabFromUrl = searchParams.get('tab');
  const activeTab = validTabs.includes(tabFromUrl) ? tabFromUrl : 'overview';

  // Update URL when tab changes
  const setActiveTab = (tabId) => {
    setSearchParams({ tab: tabId });
    trackEvent('Profile', 'Tab Change', tabId);
  };

  // Track page view on mount
  useEffect(() => {
    trackPageView('/profile', 'User Profile');
  }, []);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_URL}/user/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    trackEvent('Profile', 'Delete Account', 'confirmed');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/user/account`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        showToast('Account deleted successfully', 'success');
        setShowDeleteModal(false);
        onLogout();
      } else {
        showToast('Failed to delete account', 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showToast('Error deleting account', 'error');
    }
  };

  const handleLogout = () => {
    trackLogout();
    onLogout();
  };

  // Get current tab label
  const currentTabLabel = tabs.find(t => t.id === activeTab)?.label || 'Overview';

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" role="status" aria-label="Loading profile">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" aria-hidden="true"></div>
        <span className="sr-only">Loading profile...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-0 sm:px-6 py-6 md:py-8">

        {/* HEADER */}
        <header className="bg-white rounded-xl shadow border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            {/* User Info */}
            <div className="flex items-center space-x-4">
              <div
                className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0"
                aria-hidden="true"
              >
                <span className="text-white text-xl sm:text-2xl font-bold">
                  {user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                  {user?.email}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    user?.is_premium
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    <Crown size={12} className="mr-1" aria-hidden="true" />
                    {user?.plan_tier || 'Free'} Tier
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    <Zap size={12} className="mr-1" aria-hidden="true" />
                    {user?.credits || 0} credits
                  </span>
                </div>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="flex items-center justify-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
              aria-label="Logout from your account"
            >
              <LogOut size={18} aria-hidden="true" />
              <span>Logout</span>
            </button>
          </div>
        </header>

        {/* TABS NAVIGATION */}
        <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden mb-6">

          {/* Mobile: Dropdown with "Tap to switch" style */}
          <div className="block sm:hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b-2 border-blue-500">
              <span className="font-semibold text-gray-900">{currentTabLabel}</span>
              <div className="relative">
                <label htmlFor="tab-select" className="sr-only">Select tab</label>
                <div className="flex items-center space-x-1 text-blue-600 cursor-pointer">
                  <span className="text-sm font-medium">Tap to switch</span>
                  <ChevronDown size={18} />
                </div>
                <select
                  id="tab-select"
                  value={activeTab}
                  onChange={(e) => setActiveTab(e.target.value)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  aria-label="Navigation tabs"
                >
                  {tabs.map((tab) => (
                    <option key={tab.id} value={tab.id}>
                      {tab.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Desktop: Horizontal Tabs */}
          <nav className="hidden sm:flex border-b border-gray-200" role="tablist" aria-label="Profile sections">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-4 font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  aria-controls={`panel-${tab.id}`}
                  id={`tab-${tab.id}`}
                >
                  <Icon size={18} aria-hidden="true" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Tab Content */}
          <div
            className="p-4 sm:p-6"
            role="tabpanel"
            aria-labelledby={`tab-${activeTab}`}
            id={`panel-${activeTab}`}
          >
            {activeTab === 'overview' && <OverviewTab stats={stats} user={user} />}
            {activeTab === 'history' && <HistoryTab user={user} />}
            {activeTab === 'billing' && <BillingTab user={user} />}
            {activeTab === 'invoices' && <InvoicesTab />}
            {activeTab === 'settings' && (
              <SettingsTab
                user={user}
                onDeleteAccount={() => {
                  trackEvent('Profile', 'Delete Account', 'initiated');
                  setShowDeleteModal(true);
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        title="Delete Account?"
        message="Are you sure you want to delete your account?\n\nThis will permanently delete:\n• Your profile and credits\n• All comparison history\n• All group chat history\n• All tone lab results\n\nThis action cannot be undone!"
        onConfirm={handleDeleteAccount}
        onCancel={() => setShowDeleteModal(false)}
        type="danger"
      />
    </div>
  );
}


// ==========================================
// OVERVIEW TAB - ✅ ORIGINAL STYLING
// ==========================================
function OverviewTab({ stats, user }) {
  return (
    <div className="space-y-6">
      {/* Stats Grid - All Features */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" role="group" aria-label="Account statistics">
        {/* Credits */}
        <div className="bg-blue-50 rounded-xl p-4 sm:p-5 border border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <Zap className="text-blue-600" size={22} aria-hidden="true" />
            <span className="text-xs text-blue-600 font-medium">Available</span>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-blue-900">{user?.credits || 0}</p>
          <p className="text-xs sm:text-sm text-blue-700 mt-1">Credits</p>
        </div>

        {/* Comparisons */}
        <div className="bg-green-50 rounded-xl p-4 sm:p-5 border border-green-200">
          <div className="flex items-center justify-between mb-2">
            <BarChart3 className="text-green-600" size={22} aria-hidden="true" />
            <span className="text-xs text-green-600 font-medium">Compare</span>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-green-900">{stats?.total_runs || 0}</p>
          <p className="text-xs sm:text-sm text-green-700 mt-1">Comparisons</p>
        </div>

        {/* Chats */}
        <div className="bg-sky-50 rounded-xl p-4 sm:p-5 border border-sky-200">
          <div className="flex items-center justify-between mb-2">
            <MessageCircle className="text-sky-600" size={22} aria-hidden="true" />
            <span className="text-xs text-sky-600 font-medium">Chat</span>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-sky-900">{stats?.total_chats || 0}</p>
          <p className="text-xs sm:text-sm text-sky-700 mt-1">Group Chats</p>
        </div>

        {/* Tones */}
        <div className="bg-rose-50 rounded-xl p-4 sm:p-5 border border-rose-200">
          <div className="flex items-center justify-between mb-2">
            <FlaskConical className="text-rose-600" size={22} aria-hidden="true" />
            <span className="text-xs text-rose-600 font-medium">Tone</span>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-rose-900">{stats?.total_tones || 0}</p>
          <p className="text-xs sm:text-sm text-rose-700 mt-1">Tone Rewrites</p>
        </div>
      </div>

      {/* This Month */}
      <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="text-purple-600" size={18} aria-hidden="true" />
            <span className="font-semibold text-purple-900">This Month</span>
          </div>
          <span className="text-2xl font-bold text-purple-900">{stats?.this_month || 0}</span>
        </div>
        <p className="text-sm text-purple-700 mt-1">Total runs across all features</p>
      </div>

      {/* Recent Activity */}
      <section className="bg-white rounded-xl border border-gray-200 overflow-hidden" aria-labelledby="recent-activity-heading">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 id="recent-activity-heading" className="font-semibold text-gray-900">Recent Activity</h3>
        </div>
        <div className="divide-y divide-gray-200" role="list">
          {stats?.recent_runs?.length > 0 ? (
            stats.recent_runs.slice(0, 8).map((run, idx) => {
              const type = run.type || (run.category?.toLowerCase().includes('chat') ? 'chat' : run.category?.toLowerCase().includes('tone') ? 'tone' : 'compare');
              const badges = {
                compare: { label: 'Compare', color: 'bg-green-100 text-green-700', icon: '⚡' },
                chat: { label: 'Chat', color: 'bg-sky-100 text-sky-700', icon: '💬' },
                tone: { label: 'Tone', color: 'bg-rose-100 text-rose-700', icon: '🧪' }
              };
              const badge = badges[type] || badges.compare;
              return (
                <div key={idx} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50" role="listitem">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${badge.color}`}>
                        {badge.icon} {badge.label}
                      </span>
                      <span className="font-medium text-gray-900 truncate text-sm">{run.model || run.category || 'Run'}</span>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{run.prompt || run.topic || ''}</p>
                  </div>
                  <time className="text-xs text-gray-400 ml-4 flex-shrink-0" dateTime={run.created_at}>
                    {run.created_at ? new Date(run.created_at).toLocaleDateString() : ''}
                  </time>
                </div>
              );
            })
          ) : (
            <div className="px-4 py-8 text-center text-gray-500">
              <BarChart3 className="mx-auto mb-2 opacity-50" size={32} aria-hidden="true" />
              <p>No recent activity</p>
              <p className="text-sm">Use Compare, Chat, or Tone to see activity here</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}


// ==========================================
// HISTORY TAB - ✅ SIMPLIFIED (no latency/quality)
// ==========================================
function HistoryTab({ user }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');

  useEffect(() => {
    loadHistory();
  }, [timeRange]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/user/history?range=${timeRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoading(false);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!history.length) return;

    const headers = ['Date', 'Feature', 'Model', 'Details'];
    const rows = history.map(item => [
      item.timestamp ? new Date(item.timestamp).toLocaleString() : '',
      item.type || 'compare',
      item.model || '',
      (item.prompt || item.topic || item.category || '').substring(0, 100).replace(/"/g, '""')
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(c => `"${c}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `usage-history-${timeRange}.csv`;
    a.click();

    trackExport('usage_history', 'csv');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12" role="status" aria-label="Loading history">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" aria-hidden="true"></div>
        <span className="sr-only">Loading history...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-2">
          <History className="text-blue-600" size={20} aria-hidden="true" />
          <h3 className="font-bold text-gray-900">Usage History</h3>
          <span className="text-sm text-gray-500">({history.length} items)</span>
        </div>

        <div className="flex items-center space-x-3">
          <label htmlFor="time-range" className="sr-only">Time range</label>
          <select
            id="time-range"
            value={timeRange}
            onChange={(e) => {
              setTimeRange(e.target.value);
              trackEvent('Profile', 'Change History Range', e.target.value);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="day">Last 24 Hours</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>

          <button
            onClick={exportToCSV}
            disabled={!history.length}
            className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Export history to CSV"
          >
            <Download size={16} aria-hidden="true" />
            <span className="hidden sm:inline">Export CSV</span>
          </button>
        </div>
      </div>

      {/* History List */}
      {history.length === 0 ? (
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-8 text-center">
          <History className="mx-auto mb-3 text-gray-300" size={48} aria-hidden="true" />
          <p className="text-gray-600 font-medium">No usage history found</p>
          <p className="text-sm text-gray-500 mt-1">
            Your Compare, Chat, and Tone activity will appear here
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Desktop Table - SIMPLIFIED */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full" role="table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                  <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Feature</th>
                  <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Model</th>
                  <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {history.map((item, idx) => {
                  const type = item.type || 'compare';
                  const badges = {
                    compare: { label: 'Compare', color: 'bg-green-100 text-green-700' },
                    chat: { label: 'Chat', color: 'bg-sky-100 text-sky-700' },
                    tone: { label: 'Tone', color: 'bg-rose-100 text-rose-700' }
                  };
                  const badge = badges[type] || badges.compare;
                  return (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {item.timestamp ? new Date(item.timestamp).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <ModelLogo modelName={item.model} size="sm" />
                        <span className="text-sm font-medium text-gray-900">{item.model}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-md truncate">{item.prompt || item.topic || item.category}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards - SIMPLIFIED */}
          <div className="md:hidden divide-y divide-gray-200">
            {history.map((item, idx) => {
              const type = item.type || 'compare';
              const badges = {
                compare: { label: 'Compare', color: 'bg-green-100 text-green-700' },
                chat: { label: 'Chat', color: 'bg-sky-100 text-sky-700' },
                tone: { label: 'Tone', color: 'bg-rose-100 text-rose-700' }
              };
              const badge = badges[type] || badges.compare;
              return (
              <div key={idx} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${badge.color}`}>
                      {badge.label}
                    </span>
                    <ModelLogo modelName={item.model} size="sm" />
                    <span className="font-medium text-gray-900 text-sm">{item.model}</span>
                  </div>
                  <span className="text-xs text-gray-400">
                    {item.timestamp ? new Date(item.timestamp).toLocaleDateString() : '-'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 line-clamp-2">{item.prompt || item.topic || item.category}</p>
              </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ==========================================
// BILLING TAB - ✅ DYNAMIC FROM DB (credit_packs)
// ==========================================
function BillingTab({ user }) {
  const [creditPacks, setCreditPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCreditPacks();
  }, []);

  const loadCreditPacks = async () => {
    try {
      const response = await fetch(`${API_URL}/credit-packs`);
      if (response.ok) {
        const data = await response.json();
        // Sort by display_order
        const packs = (data.packs || data || []).sort((a, b) =>
          (a.display_order || 0) - (b.display_order || 0)
        );
        setCreditPacks(packs);
      }
    } catch (error) {
      console.error('Failed to load credit packs:', error);
    } finally {
      setLoading(false);
    }
  };

const handleBuyPack = async (packId) => {
  trackEvent('Pricing', 'Buy Pack Click', packId);

  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/payments/buy-pack`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ pack_id: packId })
    });

    if (response.ok) {
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else if (data.status === 'pending') {
        showToast('Please contact support to complete purchase', 'info');
      }
    } else {
      showToast('Failed to initiate purchase', 'error');
    }
  } catch (error) {
    console.error('Purchase error:', error);
    showToast('Error processing purchase', 'error');
  }
};

  if (loading) {
    return (
      <div className="flex justify-center py-8" role="status" aria-label="Loading plans">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" aria-hidden="true"></div>
        <span className="sr-only">Loading plans...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Current Status</h3>
            <p className="text-gray-600">
              You're on the <span className="font-semibold capitalize">{user?.plan_tier || 'Free'}</span> tier
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-blue-600">
              {user?.credits || 0}
            </p>
            <p className="text-sm text-gray-600">credits remaining</p>
          </div>
        </div>
      </div>

      {/* Credit Packs from DB */}
      <section aria-labelledby="packs-heading">
        <h3 id="packs-heading" className="text-lg font-bold text-gray-900 mb-4">Buy Credit Packs</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" role="list">
          {creditPacks.map((pack) => (
            <article
              key={pack.id}
              className={`relative rounded-xl p-5 border-2 transition-all ${
                pack.is_popular
                  ? 'border-purple-400 bg-purple-50 shadow-lg'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
              }`}
              role="listitem"
              aria-label={`${pack.name} pack, ${pack.price} dollars, ${pack.credits} credits`}
            >
              {/* Popular Badge */}
              {pack.is_popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center">
                    <Star size={12} className="mr-1" aria-hidden="true" />
                    Most Popular
                  </span>
                </div>
              )}

              <h4 className="font-bold text-lg text-gray-900 mt-1">{pack.name}</h4>

              {pack.description && (
                <p className="text-sm text-gray-500 mt-1">{pack.description}</p>
              )}

              <div className="mt-3 mb-4">
                <span className="text-3xl font-bold text-gray-900">${pack.price}</span>
              </div>

              <div className="flex items-center space-x-2 mb-4">
                <Zap className="text-yellow-500" size={18} aria-hidden="true" />
                <span className="font-semibold text-gray-700">{pack.credits} credits</span>
                {pack.bonus_credits > 0 && (
                  <span className="text-green-600 text-sm font-medium">+{pack.bonus_credits} bonus</span>
                )}
              </div>

              {pack.access_tier && pack.access_tier !== 'free' && (
                <p className="text-xs text-purple-600 mb-3">
                  Unlocks <span className="capitalize font-semibold">{pack.access_tier}</span> tier features
                </p>
              )}

              <button
                className={`w-full py-2.5 px-4 rounded-lg font-medium transition-colors ${
                  pack.is_popular
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                onClick={() => handleBuyPack(pack.id)}
              >
                Buy Now
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* Info */}
      <section className="bg-blue-50 rounded-xl border border-blue-200 p-6" aria-labelledby="credits-info-heading">
        <h3 id="credits-info-heading" className="font-semibold text-blue-900 mb-2">About Credits</h3>
        <ul className="space-y-2 text-sm text-blue-800" role="list">

          <li className="flex items-start">
            <Check size={16} className="mr-2 text-green-500 mt-0.5 flex-shrink-0" />
            <span>Credits work across all features: Compare, Chat, and Tone</span>
          </li>
          <li className="flex items-start">
            <Check size={16} className="mr-2 text-green-500 mt-0.5 flex-shrink-0" />
            <span>Different models cost different credits (1-120 per run)</span>
          </li>
          <li className="flex items-start">
            <Check size={16} className="mr-2 text-green-500 mt-0.5 flex-shrink-0" />
            <span>Higher tier packs unlock access to advanced models</span>
          </li>
        </ul>
      </section>
    </div>
  );
}


// ==========================================
// INVOICES TAB - ✅ ORIGINAL STYLING
// ==========================================
// ==========================================
// INVOICES TAB - ✅ WITH EMAIL RECEIPT INFO
// ==========================================
function InvoicesTab() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/user/transactions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setInvoices(data.transactions || []);
      }
    } catch (error) {
      console.error('Failed to load invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8" role="status" aria-label="Loading invoices">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" aria-hidden="true"></div>
        <span className="sr-only">Loading invoices...</span>
      </div>
    );
  }

  return (
    <section className="space-y-4" aria-labelledby="invoices-heading">
      <h3 id="invoices-heading" className="font-bold text-gray-900">Billing History</h3>

      {invoices.length > 0 ? (
        <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full" role="table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                    <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
                    <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Credits</th>
                    <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                    <th scope="col" className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {invoices.map((invoice, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {invoice.date ? new Date(invoice.date).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {invoice.pack_name || invoice.pack_id}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <span className="inline-flex items-center">
                          <Zap size={14} className="mr-1 text-yellow-500" />
                          {invoice.credits}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        ${invoice.price?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                          Paid
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-200">
              {invoices.map((invoice, idx) => (
                <div key={idx} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{invoice.pack_name || invoice.pack_id}</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                      Paid
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                    <span>{invoice.date ? new Date(invoice.date).toLocaleDateString() : '-'}</span>
                    <span className="font-medium text-gray-900">${invoice.price?.toFixed(2) || '0.00'}</span>
                  </div>
                  <span className="inline-flex items-center text-sm text-gray-600">
                    <Zap size={14} className="mr-1 text-yellow-500" />
                    {invoice.credits} credits
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Receipt Info */}
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4 text-center">
            <p className="text-sm text-blue-800">
              📧 Need a receipt? Check your email from <strong>midolabs.ai (via Lemon Squeezy)</strong> - it contains a link to download your invoice.
            </p>
          </div>
        </>
      ) : (
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-8 text-center">
          <FileText className="mx-auto mb-3 text-gray-400" size={40} aria-hidden="true" />
          <p className="text-gray-600 font-medium">No invoices yet</p>
          <p className="text-sm text-gray-500 mt-1">
            Your billing history will appear here after your first purchase
          </p>
        </div>
      )}
    </section>
  );
}



// ==========================================
// SETTINGS TAB - ✅ FIXED: Real password reset + colors
// ==========================================
function SettingsTab({ user, onDeleteAccount }) {
  const [isResetting, setIsResetting] = useState(false);

  const handleChangePassword = async () => {
    if (isResetting) return;

    setIsResetting(true);
    trackEvent('Profile', 'Change Password');

    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user?.email })
      });

      if (response.ok) {
        showToast('Password reset email sent! Check your inbox.', 'success');
      } else {
        const data = await response.json();
        showToast(data.detail || 'Failed to send reset email', 'error');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      showToast('Error sending reset email', 'error');
    } finally {
      setIsResetting(false);
    }
  };

  // Get tier color
  const getTierStyle = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'premium':
        return 'bg-purple-100 text-purple-700 border border-purple-300';
      case 'pro':
        return 'bg-blue-100 text-blue-700 border border-blue-300';
      case 'starter':
        return 'bg-green-100 text-green-700 border border-green-300';
      default:
        return 'bg-gray-100 text-gray-700 border border-gray-300';
    }
  };

  return (
    <div className="space-y-6">
      {/* Account Info */}
      <section className="bg-white rounded-xl border border-gray-200 p-6" aria-labelledby="account-info-heading">
        <h3 id="account-info-heading" className="font-bold text-gray-900 mb-4">Account Information</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Email Address</label>
            <p className="text-gray-900 font-medium">{user?.email}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Account Type</label>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold capitalize ${getTierStyle(user?.plan_tier)}`}>
              <Crown size={14} className="mr-1.5" aria-hidden="true" />
              {user?.plan_tier || 'Free'} Tier
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Credits Balance</label>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-700 border border-yellow-300">
              <Zap size={14} className="mr-1.5" aria-hidden="true" />
              {user?.credits || 0} credits
            </span>
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="bg-white rounded-xl border border-gray-200 p-6" aria-labelledby="security-heading">
        <h3 id="security-heading" className="font-bold text-gray-900 mb-4">Security</h3>

        <p className="text-sm text-gray-600 mb-4">
          Click the button below to receive a password reset link at <strong>{user?.email}</strong>
        </p>

        <button
          className="flex items-center space-x-2 px-4 py-2.5 bg-red-100 text-red-700 border border-red-300 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          onClick={handleChangePassword}
          disabled={isResetting}
          aria-label="Send password reset email"
        >
          {isResetting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" aria-hidden="true"></div>
              <span>Sending...</span>
            </>
          ) : (
            <>
              <Shield size={18} aria-hidden="true" />
              <span>Send Password Reset Email</span>
            </>
          )}
        </button>
      </section>

      {/* Data Export */}
      <section className="bg-white rounded-xl border border-gray-200 p-6" aria-labelledby="export-heading">
        <h3 id="export-heading" className="font-bold text-gray-900 mb-4">Export Your Data</h3>
        <p className="text-sm text-gray-600 mb-4">
          Download all your data including comparison results, chat history, tone rewrites, and account information.
        </p>
        <button
          onClick={async () => {
            trackExport('user_data', 'json');
            try {
              const token = localStorage.getItem('token');
              const response = await fetch(`${API_URL}/user/data-export`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (response.ok) {
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `midolabs-export-${new Date().toISOString().split('T')[0]}.json`;
                a.click();
                URL.revokeObjectURL(url);
                showToast('Data exported successfully!', 'success');
              } else {
                showToast('Failed to export data', 'error');
              }
            } catch (error) {
              console.error('Export error:', error);
              showToast('Error exporting data', 'error');
            }
          }}
          className="flex items-center space-x-2 px-4 py-2.5 bg-green-100 text-green-700 border border-green-300 rounded-lg hover:bg-green-200 transition-colors font-medium"
        >
          <Download size={18} aria-hidden="true" />
          <span>Download My Data</span>
        </button>
      </section>

      {/* Danger Zone */}
      <section className="bg-red-50 rounded-xl border border-red-200 p-6" aria-labelledby="danger-heading">
        <h3 id="danger-heading" className="font-bold text-red-900 mb-2">Danger Zone</h3>
        <p className="text-sm text-red-700 mb-4">
          Once you delete your account, there is no going back. Please be certain.
        </p>
        <button
          onClick={onDeleteAccount}
          className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          aria-label="Delete your account permanently"
        >
          <Trash2 size={18} aria-hidden="true" />
          <span>Delete Account</span>
        </button>
      </section>
    </div>
  );
}

export default UserProfile;